/** Small, non-modal Gym guidance. The simulation model owns outcomes; this owns device words and DOM. */
import type { GymTutorialAction, GymTutorialView } from "./gym-tutorial";

export type TutorialDevice = "keyboard" | "touch" | "pad";
type PadButtons = { jump: string; tuck: string; reach: string; pull: string; rotate: string };

function text(el: HTMLElement, value: string): void {
  if (el.textContent !== value) el.textContent = value;
}

export class GymTutorialHud {
  readonly el = document.createElement("section");
  private readonly content = document.createElement("div");
  private readonly title = document.createElement("span");
  private readonly progress = document.createElement("span");
  private readonly instruction = document.createElement("div");
  private readonly feedback = document.createElement("div");
  private readonly toggle = document.createElement("button");
  private lastView: GymTutorialView | null | undefined;
  private lastEnabled: boolean | undefined;
  private lastDevice: TutorialDevice | undefined;
  private lastPad: PadButtons | undefined;
  private lastLinkAssist: boolean | undefined;

  constructor(parent: HTMLElement, onToggle: () => void) {
    this.el.className = "gym-tutorial";
    this.el.setAttribute("aria-label", "Gym tutorial");
    this.el.hidden = true;
    this.content.className = "gym-tutorial-content";
    const heading = document.createElement("div");
    heading.className = "gym-tutorial-heading";
    this.title.className = "gym-tutorial-title";
    this.progress.className = "gym-tutorial-progress";
    heading.append(this.title, this.progress);
    this.instruction.className = "gym-tutorial-instruction";
    this.feedback.className = "gym-tutorial-feedback";
    this.feedback.setAttribute("role", "status");
    this.content.append(heading, this.instruction, this.feedback);
    this.toggle.type = "button";
    this.toggle.className = "gym-tutorial-toggle";
    this.toggle.addEventListener("click", (event) => {
      event.preventDefault();
      onToggle();
      // Pointer users return straight to playing; keyboard users keep an accessible toggle.
      if (event.detail > 0) this.toggle.blur();
    });
    for (const kind of ["keydown", "keyup"] as const)
      this.toggle.addEventListener(kind, (event) => {
        if (event.key === " " || event.key === "Enter") event.stopPropagation();
      });
    this.el.append(this.content, this.toggle);
    parent.appendChild(this.el);
  }

  show(visible: boolean): void {
    if (this.el.hidden === visible) this.el.hidden = !visible;
  }

  update(
    enabled: boolean,
    view: GymTutorialView | null,
    device: TutorialDevice,
    pad: PadButtons,
    linkAssist: boolean,
  ): void {
    if (
      view === this.lastView &&
      enabled === this.lastEnabled &&
      device === this.lastDevice &&
      pad === this.lastPad &&
      linkAssist === this.lastLinkAssist
    )
      return;
    this.lastView = view;
    this.lastEnabled = enabled;
    this.lastDevice = device;
    this.lastPad = pad;
    this.lastLinkAssist = linkAssist;
    this.content.hidden = !enabled;
    this.el.classList.toggle("is-off", !enabled);
    this.toggle.setAttribute("aria-label", enabled ? "Turn tutorial off" : "Turn tutorial on");
    this.toggle.setAttribute("aria-pressed", String(enabled));
    text(this.toggle, enabled ? "Turn off" : "Tutorial: off");
    if (!enabled || !view) return;
    this.el.dataset.lesson = view.lessonId;
    this.el.dataset.action = view.action;
    this.el.dataset.complete = String(view.completed);
    text(this.title, `${view.completed ? "✓ " : ""}${view.title}`);
    text(this.progress, `${view.completedCount}/${view.total}`);
    text(this.instruction, tutorialInstruction(view.action, device, pad, linkAssist));
    text(this.feedback, view.feedback ?? "");
    this.feedback.hidden = !view.feedback;
  }
}

function tutorialInstruction(
  action: GymTutorialAction,
  device: TutorialDevice,
  pad: PadButtons,
  linkAssist: boolean,
): string {
  const touch = device === "touch";
  const controller = device === "pad";
  // Face-button aliases are available in every scheme. One clear binding is easier to follow than
  // the full list of alternatives shown by the control settings.
  const jump = touch ? "JUMP" : controller ? pad.jump.split(" / ")[0]! : "Space";
  const tuck = touch ? "TUCK" : controller ? pad.tuck.split(" / ")[0]! : "C";
  const grab = touch ? "GRAB" : controller ? pad.reach.split(" / ")[0]! : "J";
  const move = touch || controller ? "the left stick" : "A / D";
  switch (action) {
    case "run":
      return `Use ${move} to run left or right.`;
    case "stop":
      return `Release ${move} and come to a stop.`;
    case "jump":
      return `Press ${jump} to jump. Hold for height; release for a short hop.`;
    case "land":
      return `Steer with ${move} and land on solid ground.`;
    case "slide":
      return `Run at the pipe. Hold ${tuck} all the way underneath.`;
    case "catch":
      return `Jump toward the bar with ${jump}. Hold ${grab} to catch it.`;
    case "release":
      return touch && linkAssist
        ? "As you swing forward, slide GRAB to RELEASE. Keep your finger down."
        : `Keep ${grab} held. Press ${touch ? "RELEASE" : jump} as you swing up toward the next landing.`;
    case "link":
      return touch && linkAssist
        ? "Slide back to GRAB in flight. Hold to catch the next bar."
        : `Keep ${grab} held in flight to catch the next bar.`;
    case "block":
      return `Jump onto the block with ${jump}. Steer with ${move}.`;
    case "roll":
      return touch
        ? "Jump off the block. Hold TUCK and slide that thumb forward before landing."
        : `Jump off the block. Hold ${tuck} and lean forward with ${controller ? "the right stick" : "Q / E"} before landing.`;
    case "pump":
      return `Keep ${grab} held. Move ${move} with the swing to build height.`;
    case "vault":
      return `Run at the narrow box. Tap ${tuck} just before contact to vault.`;
    case "mount":
      return controller || touch
        ? `Approach the wide box slowly. Tap ${tuck} to climb onto it.`
        : "Stop near the wide box. Start moving and tap C to land on top.";
    case "hop":
      return "Run over the small step. No jump needed.";
    case "balance":
      return `Jump between platforms with ${jump}. Ease off ${move} to land on each.`;
    case "ladder-duck":
      return touch
        ? "Just before swing bottom: slide GRAB to TUCK, then back; or stick-down. PERFECT climbs."
        : `Hold ${grab}; tap ${tuck} just before the swing bottom. Each PERFECT climbs a rung.`;
    case "ladder-release":
      return touch
        ? "At the top, swing right. Hold GRAB and tap RELEASE with another finger."
        : `At the top, keep ${grab} held and press ${jump} toward the platform. Land on top.`;
    case "ledge-grab":
      return `Hold ${grab} and jump at the edge with ${jump}.`;
    case "mantle":
      return `Keep ${grab} held. Press ${jump} again to climb onto the ledge.`;
    case "done":
      return "Keep exploring. Pause lets you jump to any section.";
  }
}
